/**
 * Utilidades de color para pintar estados con contraste garantizado.
 *
 * Los estados de producto, pedido y cotización guardan un hex libre en BD
 * (`producto_estados.color`, `pedido_estados.color`). Antes se usaba ese mismo
 * hex para el fondo (con alfa) y para el texto, sin garantía de legibilidad.
 * Ahora el hex solo aporta el TONO: el texto se lleva a una luminosidad baja y
 * el fondo a una alta, con lo que el par siempre supera AA sobre blanco.
 */

export type Tono = 'good' | 'warn' | 'bad' | 'neutral' | 'accent';

type Hsl = { h: number; s: number; l: number };

export function hexToHsl(hex: string): Hsl | null {
  const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let s = m[1];
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  const r = parseInt(s.slice(0, 2), 16) / 255;
  const g = parseInt(s.slice(2, 4), 16) / 255;
  const b = parseInt(s.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return { h, s: sat, l };
}

export function hslCss(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
}

/**
 * Texto, fondo y borde legibles a partir de un hex. Un gris (saturación
 * baja) se trata como neutro para no inventar un tono.
 */
export function coloresDesdeHex(hex: string | null | undefined): { text: string; bg: string; border: string; dot: string } {
  const hsl = hex ? hexToHsl(hex) : null;
  if (!hsl || hsl.s < 0.12) {
    return { text: 'hsl(215 16% 30%)', bg: 'hsl(215 20% 94%)', border: 'hsl(215 16% 82%)', dot: 'hsl(215 12% 55%)' };
  }
  const s = Math.min(Math.max(hsl.s, 0.45), 0.85);
  // Los amarillos (h 40-70) necesitan bajar más la luminosidad para leerse.
  const textL = hsl.h > 40 && hsl.h < 75 ? 0.28 : 0.32;
  return {
    text: hslCss(hsl.h, s, textL),
    bg: hslCss(hsl.h, Math.min(s, 0.7), 0.93),
    border: hslCss(hsl.h, Math.min(s, 0.6), 0.8),
    dot: hslCss(hsl.h, s, 0.45),
  };
}

export const TONOS: Record<Tono, { text: string; bg: string; border: string; dot: string }> = {
  good: { text: '#14743D', bg: '#DDF3E4', border: '#A9DDBC', dot: '#1E9A52' },
  warn: { text: '#A15A05', bg: '#FCEFD2', border: '#F1D08E', dot: '#D08A17' },
  bad: { text: '#B42323', bg: '#FBE1E1', border: '#F1B3B3', dot: '#D64545' },
  neutral: { text: '#3D4856', bg: '#E9EDF2', border: '#CDD5DE', dot: '#7C8794' },
  accent: { text: '#2456B8', bg: '#E3EBFA', border: '#A9BFEA', dot: '#2456B8' },
};
